import { useMemo, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import SearchIcon from '@mui/icons-material/Search'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  mitigationService,
  AtRiskRow,
  GivenUpRow,
  MitigationRung,
} from '../api/mitigationService'
import MitigationTimelineDialog, {
  RUNG_LABELS,
  formatHoursToDeadline,
} from '../components/MitigationTimelineDialog'
import ManualPlacementDialog from '../components/ManualPlacementDialog'
import AtRiskQuickActions from '../components/AtRiskQuickActions'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import PageRefreshButton from '../components/PageRefreshButton'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { fmtDateTime } from '../utils/propertyFormat'
import { getAtRiskGuidance, getGivenUpGuidance, hasOutstandingReprice } from '../utils/mitigationOpsUtils'
import { useTranslation } from 'react-i18next'

function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function endOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}

function toIsoLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function yesterdayRange(): { from: string; to: string } {
  const y = new Date()
  y.setDate(y.getDate() - 1)
  return { from: toIsoLocal(startOfDay(y)), to: toIsoLocal(endOfDay(y)) }
}

function todayRange(): { from: string; to: string } {
  const now = new Date()
  return { from: toIsoLocal(startOfDay(now)), to: toIsoLocal(endOfDay(now)) }
}

function last7DaysRange(): { from: string; to: string } {
  const to = endOfDay(new Date())
  const from = startOfDay(new Date())
  from.setDate(from.getDate() - 6)
  return { from: toIsoLocal(from), to: toIsoLocal(to) }
}

function tomorrowRange(): { from: string; to: string } {
  const t = new Date()
  t.setDate(t.getDate() + 1)
  return { from: toIsoLocal(startOfDay(t)), to: toIsoLocal(endOfDay(t)) }
}

function next7DaysRange(): { from: string; to: string } {
  const from = startOfDay(new Date())
  const to = endOfDay(new Date())
  to.setDate(to.getDate() + 6)
  return { from: toIsoLocal(from), to: toIsoLocal(to) }
}

function last30DaysRange(): { from: string; to: string } {
  const to = endOfDay(new Date())
  const from = startOfDay(new Date())
  from.setDate(from.getDate() - 29)
  return { from: toIsoLocal(from), to: toIsoLocal(to) }
}

type FunnelWindowPreset = 'today' | 'yesterday' | 'last7' | 'tomorrow' | 'next7'
type GivenUpWindowPreset = 'last7' | 'last30' | 'next7'

function funnelRangeForPreset(preset: FunnelWindowPreset): { from: string; to: string } {
  switch (preset) {
    case 'today':
      return todayRange()
    case 'yesterday':
      return yesterdayRange()
    case 'last7':
      return last7DaysRange()
    case 'tomorrow':
      return tomorrowRange()
    case 'next7':
      return next7DaysRange()
  }
}

function givenUpRangeForPreset(preset: GivenUpWindowPreset): { from: string; to: string } {
  switch (preset) {
    case 'last7':
      return last7DaysRange()
    case 'last30':
      return last30DaysRange()
    case 'next7':
      return next7DaysRange()
  }
}

const GIVEN_UP_WINDOW_LABELS: Record<GivenUpWindowPreset, string> = {
  last7: 'last 7 days',
  last30: 'last 30 days',
  next7: 'next 7 days',
}

function rungChipColor(rung?: MitigationRung | null): 'default' | 'primary' | 'secondary' | 'info' {
  if (!rung) return 'default'
  if (rung === 'REPRICE') return 'secondary'
  if (rung === 'WIDEN_DISPATCH') return 'primary'
  return 'info'
}

function urgencyChipColor(urgency?: string): 'default' | 'warning' | 'error' {
  if (urgency === 'EMERGENCY_URGENT') return 'error'
  if (urgency === 'URGENT') return 'warning'
  return 'default'
}

function shipperLabel(row: {
  shipperBusinessName?: string
  shipperName?: string
  title?: string
  shipperAccountId?: string
}) {
  return (
    row.shipperBusinessName ||
    row.shipperName ||
    row.title ||
    row.shipperAccountId?.substring(0, 8) ||
    '—'
  )
}

function hoursColor(h?: number): 'default' | 'warning' | 'error' {
  if (h == null) return 'default'
  if (h < 2) return 'error'
  if (h < 6) return 'warning'
  return 'default'
}

type AtRiskDeadlinePreset = '3h' | '6h' | '12h' | '24h' | 'next7'

const AT_RISK_DEADLINE_OPTIONS: {
  value: AtRiskDeadlinePreset
  label: string
  withinHours: number
}[] = [
  { value: '3h', label: '3 hours', withinHours: 3 },
  { value: '6h', label: '6 hours', withinHours: 6 },
  { value: '12h', label: '12 hours', withinHours: 12 },
  { value: '24h', label: '24 hours', withinHours: 24 },
  { value: 'next7', label: 'Next 7 days', withinHours: 7 * 24 },
]

function atRiskPresetToWithinHours(preset: AtRiskDeadlinePreset): number {
  return AT_RISK_DEADLINE_OPTIONS.find((o) => o.value === preset)?.withinHours ?? 6
}

function atRiskPresetLabel(preset: AtRiskDeadlinePreset): string {
  return AT_RISK_DEADLINE_OPTIONS.find((o) => o.value === preset)?.label ?? preset
}

// ---------------- At Risk tab ----------------

function AtRiskTab({
  onOpenTimeline,
  onManualPlacement,
}: {
  onOpenTimeline: (id: string) => void
  onManualPlacement: (id: string) => void
}) {
  const [deadlinePreset, setDeadlinePreset] = useState<AtRiskDeadlinePreset>('6h')
  const withinHours = atRiskPresetToWithinHours(deadlinePreset)
  const [page, setPage] = useState(0)
  const pageSize = 20

  const theme = useTheme()

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['mitigation-at-risk', withinHours, page],
    queryFn: () => mitigationService.getAtRisk(withinHours, page, pageSize),
    refetchInterval: 120_000,
  })

  const rows: AtRiskRow[] = data?.content || []

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label="Deadline within"
          value={deadlinePreset}
          onChange={(e) => {
            setDeadlinePreset(e.target.value as AtRiskDeadlinePreset)
            setPage(0)
          }}
          sx={{ minWidth: 160 }}
        >
          {AT_RISK_DEADLINE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Unplaced requests with deadline approaching — work here is cheaper than the give-up queue.
        </Typography>
        <PageRefreshButton onRefresh={() => refetch()} loading={isFetching} />
      </Stack>

      {error && <ErrorCard message="Failed to load at-risk queue" onRetry={() => refetch()} />}
      {isLoading && <TableSkeleton rows={8} columns={10} />}
      {!isLoading && !error && rows.length === 0 && (
        <EmptyState
          icon={<WarningAmberIcon sx={{ fontSize: 48, opacity: 0.4 }} />}
          title="No at-risk requests"
          description={`Nothing unplaced with deadline within ${atRiskPresetLabel(deadlinePreset).toLowerCase()}.`}
        />
      )}
      {!isLoading && !error && rows.length > 0 && (
        <>
          <TableContainer component={Paper} sx={{ flex: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <ResizableTableCell columnKey="atRisk.timeLeft" defaultWidth={90}>
                    Time left
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="atRisk.shipper" defaultWidth={160}>
                    Shipper
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="atRisk.pickup" defaultWidth={150}>
                    Pickup
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="atRisk.urgency" defaultWidth={100}>
                    Urgency
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="atRisk.currentRung" defaultWidth={140}>
                    Current rung
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="atRisk.rungs" defaultWidth={180}>
                    Rungs tried
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="atRisk.offers" defaultWidth={80}>
                    Offers
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="atRisk.round" defaultWidth={80}>
                    Round
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="atRisk.suggested" defaultWidth={200}>
                    Suggested action
                  </ResizableTableCell>
                  <ResizableTableCell
                    columnKey="atRisk.actions"
                    defaultWidth={140}
                    sx={stickyActionsHeaderSx}
                  >
                    Quick actions
                  </ResizableTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const guidance = getAtRiskGuidance(row)
                  return (
                  <TableRow
                    key={row.shipmentRequestId}
                    hover
                    sx={
                      guidance.severity === 'error'
                        ? { bgcolor: alpha(theme.palette.error.main, 0.08) }
                        : guidance.severity === 'warning'
                          ? { bgcolor: alpha(theme.palette.warning.main, 0.08) }
                          : undefined
                    }
                  >
                    <TableCell>
                      <Chip
                        label={formatHoursToDeadline(row.hoursToDeadline)}
                        size="small"
                        color={hoursColor(row.hoursToDeadline)}
                      />
                    </TableCell>
                    <TableCell>{shipperLabel(row)}</TableCell>
                    <TableCell>{fmtDateTime(row.pickupWindowStart)}</TableCell>
                    <TableCell>
                      {row.urgency ? (
                        <Chip label={row.urgency} size="small" color={urgencyChipColor(row.urgency)} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        {row.currentRung ? (
                          <Chip
                            label={RUNG_LABELS[row.currentRung] || row.currentRung}
                            size="small"
                            color={rungChipColor(row.currentRung)}
                          />
                        ) : (
                          '—'
                        )}
                        {hasOutstandingReprice(row) && (
                          <Chip label="Reprice pending" size="small" color="secondary" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.25 }}>
                        {(row.rungsAttempted || []).map((r) => (
                          <Chip key={r} label={RUNG_LABELS[r] || r} size="small" variant="outlined" />
                        ))}
                        {(row.rungsAttempted || []).length === 0 && '—'}
                      </Stack>
                    </TableCell>
                    <TableCell>{row.offersSent ?? '—'}</TableCell>
                    <TableCell>{row.maxDispatchRound ?? '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={guidance.detail}>
                        <Chip
                          label={guidance.label}
                          size="small"
                          color={
                            guidance.severity === 'error'
                              ? 'error'
                              : guidance.severity === 'warning'
                                ? 'warning'
                                : 'default'
                          }
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={stickyActionsBodySx}>
                      <AtRiskQuickActions
                        row={row}
                        onOpenTimeline={onOpenTimeline}
                        onManualPlacement={onManualPlacement}
                      />
                    </TableCell>
                  </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {(data?.totalPages ?? 0) > 1 && (
            <Pagination
              count={data?.totalPages ?? 1}
              page={page + 1}
              onChange={(_, p) => setPage(p - 1)}
              color="primary"
              size="small"
            />
          )}
        </>
      )}
    </Stack>
  )
}

// ---------------- Given Up tab ----------------

function GivenUpTab({
  onOpenTimeline,
  datePreset,
  onDatePresetChange,
}: {
  onOpenTimeline: (id: string) => void
  datePreset: GivenUpWindowPreset
  onDatePresetChange: (v: GivenUpWindowPreset) => void
}) {
  const [page, setPage] = useState(0)
  const pageSize = 20
  const range = useMemo(() => givenUpRangeForPreset(datePreset), [datePreset])

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['mitigation-given-up', range.from, range.to, page],
    queryFn: () =>
      mitigationService.getGivenUp({
        from: range.from,
        to: range.to,
        handled: false,
        page,
        size: pageSize,
      }),
    refetchInterval: 120_000,
  })

  const rows: GivenUpRow[] = data?.content || []
  const givenUpGuidance = getGivenUpGuidance()

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      <Alert severity={givenUpGuidance.severity}>
        <Typography variant="subtitle2">{givenUpGuidance.label}</Typography>
        <Typography variant="body2">{givenUpGuidance.detail}</Typography>
      </Alert>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label="Window"
          value={datePreset}
          onChange={(e) => {
            onDatePresetChange(e.target.value as GivenUpWindowPreset)
            setPage(0)
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="next7">Next 7 days</MenuItem>
          <MenuItem value="last7">Last 7 days</MenuItem>
          <MenuItem value="last30">Last 30 days</MenuItem>
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Unresolved give-ups — soonest pickup first. Always open the timeline before calling the
          shipper.
        </Typography>
        <PageRefreshButton onRefresh={() => refetch()} loading={isFetching} />
      </Stack>

      {error && <ErrorCard message="Failed to load give-up queue" onRetry={() => refetch()} />}
      {isLoading && <TableSkeleton rows={8} columns={8} />}
      {!isLoading && !error && rows.length === 0 && (
        <EmptyState
          icon={<TrendingDownIcon sx={{ fontSize: 48, opacity: 0.4 }} />}
          title="Give-up queue is empty"
          description={`No unresolved give-ups in the ${GIVEN_UP_WINDOW_LABELS[datePreset]}.`}
        />
      )}
      {!isLoading && !error && rows.length > 0 && (
        <>
          <TableContainer component={Paper} sx={{ flex: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <ResizableTableCell columnKey="givenUp.requestId" defaultWidth={120}>
                    Request
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="givenUp.pickup" defaultWidth={150}>
                    Pickup
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="givenUp.shipper" defaultWidth={160}>
                    Shipper
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="givenUp.givenUpAt" defaultWidth={150}>
                    Gave up at
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="givenUp.reason" defaultWidth={180}>
                    Reason
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="givenUp.urgency" defaultWidth={100}>
                    Urgency
                  </ResizableTableCell>
                  <ResizableTableCell columnKey="givenUp.status" defaultWidth={120}>
                    Status
                  </ResizableTableCell>
                  <ResizableTableCell
                    columnKey="givenUp.actions"
                    defaultWidth={72}
                    sx={stickyActionsHeaderSx}
                  >
                    Actions
                  </ResizableTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.shipmentRequestId} hover>
                    <TableCell>
                      <Tooltip title={row.shipmentRequestId}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {row.shipmentRequestId.substring(0, 8)}…
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{fmtDateTime(row.pickupWindowStart)}</TableCell>
                    <TableCell>{shipperLabel(row)}</TableCell>
                    <TableCell>{fmtDateTime(row.givenUpAt)}</TableCell>
                    <TableCell>{row.giveUpReason || '—'}</TableCell>
                    <TableCell>
                      {row.urgency ? (
                        <Chip label={row.urgency} size="small" color={urgencyChipColor(row.urgency)} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{row.status || '—'}</TableCell>
                    <TableCell sx={stickyActionsBodySx}>
                      <Tooltip title="Open timeline & actions">
                        <IconButton size="small" onClick={() => onOpenTimeline(row.shipmentRequestId)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(data?.totalPages ?? 0) > 1 && (
            <Pagination
              count={data?.totalPages ?? 1}
              page={page + 1}
              onChange={(_, p) => setPage(p - 1)}
              color="primary"
              size="small"
            />
          )}
        </>
      )}
    </Stack>
  )
}

// ---------------- Funnel tab ----------------

function FunnelTab({ onGoToGivenUp }: { onGoToGivenUp: () => void }) {
  const [preset, setPreset] = useState<FunnelWindowPreset>('today')
  const [urgencyFilter, setUrgencyFilter] = useState('')
  const [accountIdFilter, setAccountIdFilter] = useState('')
  const range = funnelRangeForPreset(preset)

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['mitigation-funnel', range.from, range.to, urgencyFilter, accountIdFilter],
    queryFn: () =>
      mitigationService.getFunnel({
        ...range,
        urgency: urgencyFilter || undefined,
        accountId: accountIdFilter.trim() || undefined,
      }),
  })

  const rungChartData = useMemo(
    () =>
      (data?.rungs || []).map((r) => ({
        name: RUNG_LABELS[r.rung] || r.rung,
        entered: r.entered,
        resolved: r.resolved ?? 0,
        resolvedRate: r.resolvedRate != null ? Math.round(r.resolvedRate * 100) : 0,
      })),
    [data],
  )

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} flexWrap="wrap">
        <TextField
          select
          size="small"
          label="Window"
          value={preset}
          onChange={(e) => setPreset(e.target.value as FunnelWindowPreset)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="today">Today</MenuItem>
          <MenuItem value="next7">Next 7 days</MenuItem>
          <MenuItem value="tomorrow">Tomorrow</MenuItem>
          <MenuItem value="yesterday">Yesterday</MenuItem>
          <MenuItem value="last7">Last 7 days</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Urgency"
          value={urgencyFilter}
          onChange={(e) => setUrgencyFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="URGENT">Urgent</MenuItem>
          <MenuItem value="EMERGENCY_URGENT">Emergency urgent</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Shipper account ID"
          value={accountIdFilter}
          onChange={(e) => setAccountIdFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <PageRefreshButton onRefresh={() => refetch()} loading={isFetching} />
      </Stack>

      <Alert severity="info" icon={false}>
        Morning review: read <strong>Entered ladder</strong>, then <strong>Gave up</strong>, then{' '}
        <strong>Lost after give-up</strong> — the only true failure metric.
      </Alert>

      {error && <ErrorCard message="Failed to load funnel" onRetry={() => refetch()} />}
      {isLoading && <TableSkeleton rows={4} columns={4} />}

      {data && !isLoading && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Entered ladder" value={data.enteredLadder} highlight="warning" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Gave up" value={data.gaveUp?.total} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                label="Lost after give-up"
                value={data.lostAfterGiveUp}
                highlight={(data.lostAfterGiveUp ?? 0) > 0 ? 'error' : undefined}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Ops placed" value={data.opsPlaced} highlight="success" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Considered" value={data.requestsConsidered} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Matched (no ladder)" value={data.matchedWithoutLadder} />
            </Grid>
          </Grid>

          {(data.lostAfterGiveUp ?? 0) > 0 && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" endIcon={<ArrowForwardIcon />} onClick={onGoToGivenUp}>
                  Review give-up queue
                </Button>
              }
            >
              {data.lostAfterGiveUp ?? 0} shipper{(data.lostAfterGiveUp ?? 0) > 1 ? 's' : ''} lost after
              give-up — open each case from the give-up queue and ask: was it reachable at any point?
            </Alert>
          )}

          {rungChartData.length > 0 && (
            <Paper sx={{ p: 2, height: 320 }}>
              <Typography variant="subtitle2" gutterBottom>
                Rung funnel — entered vs resolved
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={rungChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="entered" fill="#6366f1" name="Entered" />
                  <Bar dataKey="resolved" fill="#22c55e" name="Resolved" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rung</TableCell>
                  <TableCell align="right">Entered</TableCell>
                  <TableCell align="right">Resolved</TableCell>
                  <TableCell align="right">Resolved rate</TableCell>
                  <TableCell align="right">Proposed</TableCell>
                  <TableCell align="right">Approved</TableCell>
                  <TableCell align="right">Declined</TableCell>
                  <TableCell align="right">Expired</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.rungs || []).map((r) => (
                  <TableRow key={r.rung}>
                    <TableCell>{RUNG_LABELS[r.rung] || r.rung}</TableCell>
                    <TableCell align="right">{r.entered}</TableCell>
                    <TableCell align="right">{r.resolved ?? '—'}</TableCell>
                    <TableCell align="right">
                      {r.resolvedRate != null ? `${Math.round(r.resolvedRate * 100)}%` : '—'}
                    </TableCell>
                    <TableCell align="right">{r.proposed ?? '—'}</TableCell>
                    <TableCell align="right">{r.approved ?? '—'}</TableCell>
                    <TableCell align="right">{r.declined ?? '—'}</TableCell>
                    <TableCell align="right">{r.expired ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {Object.keys(data.gaveUp?.byReason ?? {}).length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Give-up reasons
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                {Object.entries(data.gaveUp?.byReason ?? {}).map(([reason, count]) => (
                  <Chip key={reason} label={`${reason}: ${count}`} size="small" />
                ))}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Stack>
  )
}

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string
  value?: number | null
  highlight?: 'success' | 'warning' | 'error'
}) {
  const safeValue = value ?? 0
  const color =
    highlight === 'error'
      ? 'error.main'
      : highlight === 'success'
        ? 'success.main'
        : highlight === 'warning'
          ? 'warning.main'
          : 'text.primary'

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700} color={color}>
          {safeValue.toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  )
}

// ---------------- Offer Rounds tab ----------------

function OfferRoundsTab() {
  const range = useMemo(() => last7DaysRange(), [])

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['mitigation-offer-rounds', range.from, range.to],
    queryFn: () => mitigationService.getOfferRounds(range.from, range.to),
  })

  const chartData = useMemo(
    () =>
      (data || []).map((r) => ({
        round: `R${r.round}`,
        sent: r.offersSent,
        accepted: r.offersAccepted,
        rate: r.acceptanceRate != null ? Math.round(r.acceptanceRate * 100) : 0,
      })),
    [data],
  )

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Per-round offer acceptance — if round 4 converts far worse than round 1, doubling may be
        buying reach at the cost of Freighter attention.
      </Typography>
      <PageRefreshButton onRefresh={() => refetch()} loading={isFetching} />

      {error && <ErrorCard message="Failed to load offer rounds" onRetry={() => refetch()} />}
      {isLoading && <TableSkeleton rows={4} columns={4} />}

      {data && data.length > 0 && (
        <>
          <Paper sx={{ p: 2, height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="round" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" unit="%" />
                <RechartsTooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="sent" stroke="#6366f1" name="Sent" />
                <Line yAxisId="left" type="monotone" dataKey="accepted" stroke="#22c55e" name="Accepted" />
                <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#f59e0b" name="Rate %" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Round</TableCell>
                  <TableCell align="right">Offers sent</TableCell>
                  <TableCell align="right">Accepted</TableCell>
                  <TableCell align="right">Acceptance rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((r) => (
                  <TableRow key={r.round}>
                    <TableCell>{r.round}</TableCell>
                    <TableCell align="right">{r.offersSent}</TableCell>
                    <TableCell align="right">{r.offersAccepted}</TableCell>
                    <TableCell align="right">
                      {r.acceptanceRate != null ? `${Math.round(r.acceptanceRate * 100)}%` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {data && data.length === 0 && !isLoading && (
        <EmptyState title="No round data yet" description="Needs a few weeks of dispatch events." />
      )}
    </Stack>
  )
}

// ---------------- Main page ----------------

export default function MitigationPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const tabFromUrl = searchParams.get('tab')
  const initialTab =
    tabFromUrl === 'given-up' ? 1 : tabFromUrl === 'funnel' ? 2 : tabFromUrl === 'offer-rounds' ? 3 : 0

  const [tab, setTab] = useState(initialTab)
  const [timelineId, setTimelineId] = useState<string | null>(searchParams.get('requestId'))
  const [placementId, setPlacementId] = useState<string | null>(null)
  const [searchDraft, setSearchDraft] = useState(searchParams.get('requestId') || '')
  const [givenUpPreset, setGivenUpPreset] = useState<GivenUpWindowPreset>('next7')

  useEffect(() => {
    const requestId = searchParams.get('requestId')
    if (requestId) {
      setTimelineId(requestId)
      setSearchDraft(requestId)
    }
  }, [searchParams])

  const openTimeline = (id: string) => {
    setTimelineId(id)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('requestId', id)
      return next
    })
  }

  const closeTimeline = () => {
    setTimelineId(null)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('requestId')
      return next
    })
  }

  const handleTabChange = (v: number) => {
    setTab(v)
    const tabName = v === 1 ? 'given-up' : v === 2 ? 'funnel' : v === 3 ? 'offer-rounds' : 'at-risk'
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', tabName)
      return next
    })
  }

  const handleSearch = () => {
    const id = searchDraft.trim()
    if (!id) return
    openTimeline(id)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t('nav.mitigationLadder')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ops desk for the mitigation ladder — at-risk sweep, give-up recovery, and funnel
            monitoring.
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          label="Search shipment request (Flow 4 — shipper called)"
          placeholder="Paste shipment request UUID…"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch} edge="end" disabled={!searchDraft.trim()}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => handleTabChange(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="At risk" />
          <Tab label="Given up" />
          <Tab label="Funnel" />
          <Tab label="Offer rounds" />
        </Tabs>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {tab === 0 && (
          <AtRiskTab onOpenTimeline={openTimeline} onManualPlacement={setPlacementId} />
        )}
        {tab === 1 && (
          <GivenUpTab
            onOpenTimeline={openTimeline}
            datePreset={givenUpPreset}
            onDatePresetChange={setGivenUpPreset}
          />
        )}
        {tab === 2 && <FunnelTab onGoToGivenUp={() => handleTabChange(1)} />}
        {tab === 3 && <OfferRoundsTab />}
      </Box>

      <MitigationTimelineDialog
        shipmentRequestId={timelineId}
        open={!!timelineId}
        onClose={closeTimeline}
      />

      {placementId && (
        <ManualPlacementDialog
          shipmentRequestId={placementId}
          open={!!placementId}
          onClose={() => setPlacementId(null)}
          onSuccess={() => setPlacementId(null)}
        />
      )}
    </Box>
  )
}
